import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

/**
 * Public invite-code validation. Called before signup so unauthenticated users
 * can check whether their code is valid. Uses the admin client because the
 * invite_codes table only grants SELECT to authenticated users.
 */
export const validateInviteCode = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => z.object({ code: z.string().trim().min(1) }).parse(d))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row, error } = await supabaseAdmin
      .from("invite_codes")
      .select("id, code, max_uses, used_count, expires_at, revoked_at")
      .eq("code", data.code.trim().toUpperCase())
      .limit(1)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!row) return { valid: false, reason: "Code not found" };
    if (row.revoked_at) return { valid: false, reason: "This code has been revoked" };
    if (row.expires_at && new Date(row.expires_at) < new Date())
      return { valid: false, reason: "This code has expired" };
    if (row.used_count >= row.max_uses)
      return { valid: false, reason: "This code has reached its usage limit" };
    return { valid: true };
  });

/**
 * Consume an invite code after a user successfully signs up. Marks the code
 * used, approves the account, and applies whatever plan the code grants.
 */
export const consumeInviteCode = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ code: z.string().trim().min(1) }).parse(d))
  .handler(async ({ data, context }) => {
    const { redeemCode } = await import("./access.server");
    return redeemCode(context.userId, data.code);
  });
